import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { store } from "../stores/Store";
import { Produto, ProdutoCategoria } from "../types/store";
import { adminStore } from "../stores/AdminStore";

function isValidImageUrl(url: string) {
    return /^https?:\/\/.*\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(url);
}

export const EditarProduto: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [produto, setProduto] = useState<Produto | null>(null);
    const [fotoProdutoUrl, setFotoProdutoUrl] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (id) {
            store.loadProdutoById(Number(id)).then((prod) => {
                setProduto(prod ?? null);
                setLoading(false);
            });
        }
    }, [id]);

    if (loading) {
        return <div>Carregando produto...</div>;
    }

    if (!produto) {
        return <div>Produto não encontrado</div>;
    }

    const handleChange = (field: keyof Produto, value: any) => {
        setProduto((prev) => (prev ? { ...prev, [field]: value } : prev));
    };

    const handleCurrencyChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        field: keyof Produto
    ) => {
        const value = e.target.value;
        // Remove everything except digits
        const numeric = value.replace(/\D/g, "");
        // Parse to number (cents)
        const number = parseInt(numeric, 10) || 0;

        setProduto((prev) => (prev ? { ...prev, [field]: number / 100 } : prev));
    };

    const handleAddFotoProduto = (e: React.FormEvent) => {
        e.preventDefault();
        if (
            fotoProdutoUrl.trim() !== "" &&
            isValidImageUrl(fotoProdutoUrl.trim())
        ) {
            setProduto((prev) =>
                prev
                    ? {
                        ...prev,
                        fotoProdutosUrls: [
                            ...prev.fotoProdutosUrls,
                            fotoProdutoUrl.trim(),
                        ],
                    }
                    : prev
            );
            setFotoProdutoUrl("");
        }
    };

    const handleRemoveFotoProduto = (idx: number) => {
        setProduto((prev) =>
            prev
                ? {
                    ...prev,
                    fotoProdutosUrls: prev.fotoProdutosUrls.filter((_, i) => i !== idx),
                }
                : prev
        );
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const success = await adminStore.atualizaProduto(produto);
        if (success) {
            store.updateProduct(produto);
            alert("Produto atualizado com sucesso!");
        } else {
            alert("Erro ao atualizar produto");
        }
    };

    // Helper for integer-only input
    const handleIntegerChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        field: keyof Produto
    ) => {
        const value = e.target.value.replace(/\D/g, ""); // Remove non-digits
        setProduto((prev) =>
            prev ? { ...prev, [field]: value === "" ? 0 : parseInt(value, 10) } : prev
        );
    };

    const inputStyle: React.CSSProperties = {
        width: "100%",
        padding: 12,
        fontSize: 18,
        boxSizing: "border-box",
    };

    const selectStyle: React.CSSProperties = {
        width: "100%",
        padding: 12,
        fontSize: 18,
        boxSizing: "border-box",
        backgroundColor: "#fff",
        color: "#212529",
    };

    return (
        <div
            style={{
                minHeight: "80vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <div
                style={{
                    padding: 32,
                    borderRadius: 10,
                    width: "100%",
                    maxWidth: 600,
                }}
            >
                <h2 style={{ textAlign: "center", marginBottom: 24 }}>
                    Editar Produto
                </h2>
                <form onSubmit={handleSubmit}>
                    {/* Nome */}
                    <div
                        style={{
                            marginBottom: 16,
                            display: "flex",
                            alignItems: "flex-end",
                            gap: 16,
                        }}
                    >
                        <div style={{ flex: 1 }}>
                            <label style={{ display: "block" }}>
                                Nome:
                                <br />
                                <input
                                    type="text"
                                    value={produto.nome}
                                    onChange={(e) => handleChange("nome", e.target.value)}
                                    style={inputStyle}
                                    required
                                />
                            </label>
                        </div>
                    </div>
                    {/* Imagem capa url */}
                    <div
                        style={{
                            marginBottom: 16,
                            display: "flex",
                            alignItems: "flex-end",
                            gap: 16,
                        }}
                    >
                        <div style={{ flex: 1 }}>
                            <label style={{ display: "block" }}>
                                Imagem capa url:
                                <input
                                    type="text"
                                    value={produto.fotoCapaUrl}
                                    onChange={(e) => handleChange("fotoCapaUrl", e.target.value)}
                                    style={inputStyle}
                                    placeholder="URL da imagem de capa"
                                    required
                                />
                            </label>
                            {isValidImageUrl(produto.fotoCapaUrl) && (
                                <div
                                    style={{
                                        width: 64,
                                        height: 64,
                                        border: "1px solid #eee",
                                        borderRadius: 6,
                                        overflow: "hidden",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        marginTop: 8,
                                    }}
                                >
                                    <img
                                        src={produto.fotoCapaUrl}
                                        alt="Preview"
                                        style={{ maxWidth: "100%", maxHeight: "100%" }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    {/* Descrição */}
                    <div
                        style={{
                            marginBottom: 16,
                            display: "flex",
                            alignItems: "flex-end",
                            gap: 16,
                        }}
                    >
                        <div style={{ flex: 1 }}>
                            <label style={{ display: "block" }}>
                                Descrição:
                                <textarea
                                    value={produto.descricao}
                                    onChange={(e) => handleChange("descricao", e.target.value)}
                                    style={{ ...inputStyle, resize: "vertical" }}
                                    rows={4}
                                    required
                                />
                            </label>
                        </div>
                    </div>
                    {/* Categoria */}
                    <div
                        style={{
                            marginBottom: 16,
                            display: "flex",
                            alignItems: "flex-end",
                            gap: 16,
                        }}
                    >
                        <div style={{ flex: 1 }}>
                            <label style={{ display: "block" }}>
                                Categoria:
                                <select
                                    value={produto.categoria}
                                    onChange={(e) =>
                                        handleChange("categoria", Number(e.target.value))
                                    }
                                    style={selectStyle}
                                >
                                    <option value={ProdutoCategoria.LOJA}>LOJA</option>
                                    <option value={ProdutoCategoria.CATALOGO}>CATALOGO</option>
                                </select>
                            </label>
                        </div>
                    </div>
                    {/* Campos extras para CATALOGO */}
                    <div
                        style={{
                            display: "flex",
                            gap: 12,
                            marginBottom: 16,
                            alignItems: "flex-end",
                        }}
                    >
                        <div style={{ flex: 1 }}>
                            <label>
                                Peso (g):
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    min="0"
                                    value={produto.peso}
                                    onChange={(e) => handleIntegerChange(e, "peso")}
                                    style={inputStyle}
                                />
                            </label>
                        </div>
                        <div style={{ flex: 1 }}>
                            <label>
                                Altura (cm):
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    min="0"
                                    value={produto.altura}
                                    onChange={(e) => handleIntegerChange(e, "altura")}
                                    style={inputStyle}
                                />
                            </label>
                        </div>
                        <div style={{ flex: 1 }}>
                            <label>
                                Comprimento (cm):
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    min="0"
                                    value={produto.comprimento}
                                    onChange={(e) => handleIntegerChange(e, "comprimento")}
                                    style={inputStyle}
                                />
                            </label>
                        </div>
                        <div style={{ flex: 1 }}>
                            <label>
                                Largura (cm):
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    min="0"
                                    value={produto.largura}
                                    onChange={(e) => handleIntegerChange(e, "largura")}
                                    style={inputStyle}
                                />
                            </label>
                        </div>
                    </div>
                    {/* Preço */}
                    <div
                        style={{
                            marginBottom: 16,
                            display: "flex",
                            alignItems: "flex-end",
                            gap: 16,
                        }}
                    >
                        <div style={{ flex: 1 }}>
                            <label style={{ display: "block" }}>
                                Preço:
                                <input
                                    id="preco"
                                    type="text"
                                    value={produto.preco.toLocaleString("pt-BR", {
                                        style: "currency",
                                        currency: "BRL",
                                    })}
                                    onChange={(e) => handleCurrencyChange(e, "preco")}
                                    style={inputStyle}
                                    inputMode="decimal"
                                    required
                                />
                            </label>
                        </div>
                    </div>
                    {/* Desconto */}
                    <div
                        style={{
                            marginBottom: 16,
                            display: "flex",
                            alignItems: "flex-end",
                            gap: 16,
                        }}
                    >
                        <div style={{ flex: 1 }}>
                            <label style={{ display: "block" }}>
                                Desconto:
                                <input
                                    id="desconto"
                                    type="text"
                                    value={produto.desconto.toLocaleString("pt-BR", {
                                        style: "currency",
                                        currency: "BRL",
                                    })}
                                    onChange={(e) => handleCurrencyChange(e, "desconto")}
                                    style={inputStyle}
                                    inputMode="decimal"
                                    required
                                />
                            </label>
                        </div>
                    </div>
                    {/* Informações Adicionais */}
                    <div
                        style={{
                            marginBottom: 16,
                            display: "flex",
                            alignItems: "flex-end",
                            gap: 16,
                        }}
                    >
                        <div style={{ flex: 1 }}>
                            <label style={{ display: "block" }}>
                                Informações Adicionais:
                                <textarea
                                    value={produto.informacaoAdicional}
                                    onChange={(e) =>
                                        handleChange("informacaoAdicional", e.target.value)
                                    }
                                    style={{ ...inputStyle, resize: "vertical" }}
                                    rows={3}
                                />
                            </label>
                        </div>
                    </div>
                    {/* Autores */}
                    <div
                        style={{
                            marginBottom: 16,
                            display: "flex",
                            alignItems: "flex-end",
                            gap: 16,
                        }}
                    >
                        <div style={{ flex: 1 }}>
                            <label style={{ display: "block" }}>
                                Autores:
                                <input
                                    type="text"
                                    value={produto.autores}
                                    onChange={(e) => handleChange("autores", e.target.value)}
                                    style={inputStyle}
                                />
                            </label>
                        </div>
                    </div>
                    {/* Fotos do produto */}
                    <div
                        style={{
                            marginBottom: 16,
                            display: "flex",
                            alignItems: "flex-end",
                            gap: 16,
                        }}
                    >
                        <div style={{ flex: 1 }}>
                            <label style={{ display: "block" }}>
                                Fotos do produto url:
                                <div style={{ display: "flex", gap: 8 }}>
                                    <input
                                        type="text"
                                        value={fotoProdutoUrl}
                                        onChange={(e) => setFotoProdutoUrl(e.target.value)}
                                        style={{ ...inputStyle, marginBottom: 0 }}
                                        placeholder="URL da foto do produto"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleAddFotoProduto}
                                        style={{
                                            padding: "0 16px",
                                            fontSize: 22,
                                            fontWeight: "bold",
                                            background: "#198754",
                                            color: "#fff",
                                            border: "none",
                                            borderRadius: 4,
                                            cursor: "pointer",
                                        }}
                                        title="Adicionar foto"
                                        disabled={!isValidImageUrl(fotoProdutoUrl)}
                                    >
                                        +
                                    </button>
                                </div>
                            </label>
                            {/* Lista de fotos adicionadas */}
                            {produto.fotoProdutosUrls.length > 0 && (
                                <ul style={{ marginTop: 8, paddingLeft: 0, listStyle: "none" }}>
                                    {produto.fotoProdutosUrls.map((url, idx) => (
                                        <li
                                            key={idx}
                                            style={{
                                                marginBottom: 8,
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 8,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: 48,
                                                    height: 48,
                                                    border: "1px solid #eee",
                                                    borderRadius: 6,
                                                    overflow: "hidden",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    background: "#fafafa",
                                                }}
                                            >
                                                <img
                                                    src={url}
                                                    alt={`Foto ${idx + 1}`}
                                                    style={{ maxWidth: "100%", maxHeight: "100%" }}
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveFotoProduto(idx)}
                                                style={{
                                                    background: "#dc3545",
                                                    color: "#fff",
                                                    border: "none",
                                                    borderRadius: 4,
                                                    cursor: "pointer",
                                                    fontWeight: "bold",
                                                    fontSize: 16,
                                                    width: 28,
                                                    height: 28,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                                title="Remover"
                                            >
                                                x
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                    {/* Quantidade em Estoque */}
                    <div
                        style={{
                            marginBottom: 16,
                            display: "flex",
                            alignItems: "flex-end",
                            gap: 16,
                        }}
                    >
                        <div style={{ flex: 1 }}>
                            <label style={{ display: "block" }}>
                                Quantidade em Estoque:
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    min="0"
                                    value={produto.quantidadeEstoque}
                                    onChange={(e) => handleIntegerChange(e, "quantidadeEstoque")}
                                    style={inputStyle}
                                />
                            </label>
                        </div>
                    </div>
                    {/* Produto ativo */}
                    <div
                        style={{
                            marginBottom: 16,
                            display: "flex",
                            alignItems: "flex-end",
                            gap: 16,
                        }}
                    >
                        <div style={{ flex: 1 }}>
                            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <input
                                    type="checkbox"
                                    checked={produto.ativo}
                                    onChange={(e) => handleChange("ativo", e.target.checked)}
                                    style={{ width: 20, height: 20 }}
                                />
                                Produto ativo
                            </label>
                        </div>
                    </div>
                    <button
                        type="submit"
                        style={{
                            width: "100%",
                            padding: 14,
                            fontSize: 18,
                            fontWeight: "bold",
                            background: "#198754",
                            color: "#fff",
                            border: "none",
                            borderRadius: 6,
                            cursor: "pointer",
                            boxShadow: "0 2px 6px rgba(25,135,84,0.08)",
                            letterSpacing: 1,
                            marginTop: 8,
                            transition: "background 0.2s",
                        }}
                        onMouseOver={(e) => (e.currentTarget.style.background = "#157347")}
                        onMouseOut={(e) => (e.currentTarget.style.background = "#198754")}
                    >
                        Salvar Alterações
                    </button>
                </form>
            </div>
        </div>
    );
};
